import { useIntl, type UserRequirement, type UserUKselfCategorisationRequirementConfig } from '@capsicum/common';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useSubmitRequirementStateByIdentifierQuery } from '../data-access';

type UkWarningFormType = z.infer<ReturnType<typeof useUkWarningSchema>>;

const useUkWarningSchema = () => {
	const { $t } = useIntl();

	return z.object({
		firstName: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('global.firstName'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				255,
				$t('requirements.common.form.errors.length', {
					field: $t('global.firstName'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			),
		lastName: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('global.lastName'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				255,
				$t('requirements.common.form.errors.length', {
					field: $t('global.lastName'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			)
	});
};

const useUkWarningForm = () => {
	const UkWarningForm = useForm<UkWarningFormType>({
		defaultValues: {
			firstName: '',
			lastName: ''
		},
		delayError: 200,
		mode: 'onChange',
		resolver: zodResolver(useUkWarningSchema())
	});

	return UkWarningForm;
};

type UseUkWarningProps = {
	requirement: UserRequirement;
};

export const useUkWarning = ({ requirement }: UseUkWarningProps) => {
	const firstName = (requirement.config as UserUKselfCategorisationRequirementConfig).prefill?.user?.firstName;
	const lastName = (requirement.config as UserUKselfCategorisationRequirementConfig).prefill?.user?.lastName;

	const [form, setForm] = useState({
		firstName: firstName || '',
		lastName: lastName || ''
	});

	const fullName = useMemo(() => {
		return `${form.firstName} ${form.lastName}`;
	}, [form.firstName, form.lastName]);

	const isFullnameFilled = useMemo(() => {
		return Boolean(form.firstName && form.lastName);
	}, [form.firstName, form.lastName]);

	const { isLoading, mutate } = useSubmitRequirementStateByIdentifierQuery();

	const ukWarningForm = useUkWarningForm();

	const handleSubmitForm = () => {
		setForm(prev => ({ ...prev, ...ukWarningForm.getValues() }));
	};

	const submitRequirement = () => {
		mutate({
			requirementData: { ...form, accepted: true },
			requirementIdentifier: requirement.identifier
		});
	};

	return {
		fullName,
		handleSubmitForm,
		isFullnameFilled,
		isSubmitLoading: isLoading,
		submitRequirement,
		ukWarningForm
	};
};
